// @ts-strict

import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TableService } from '@pages/statistics/shared/statistics-table/table.service';
import { StatisticsService } from '@pages/statistics/statistics.service';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AvailableReportsFormats, ExportReportsService } from '../export-reports.service';
import { IToggleSwitchStateData } from '@app/core/models/core-models';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IStatisticsTableColumn } from '@app/core/services/statistics/statistics.types';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';

@Component({
  selector: 'app-export-to-file',
  templateUrl: './export-to-file.component.html',
  styleUrls: ['./export-to-file.component.scss']
})
export class ExportToFileComponent implements OnInit, OnDestroy, OnChanges {
  @Input() loading = true;
  @Input() selectOptions: { id: number; name: string }[] = [];

  public availableColumnsForExport$!: Observable<{ name: string; checked: boolean }[]>;

  public saveAsReport = true;

  public checkedValues: string[] = [];
  private selectedFormat: AvailableReportsFormats = 'json';

  private destroy$ = new Subject<void>();

  constructor(
    private readonly exportReportsService: ExportReportsService,
    private readonly statisticsTableService: TableService,
    private readonly statisticsService: StatisticsService,
    private readonly globalNotificationsService: GlobalNotificationsService,
    private readonly activeModal: NgbActiveModal
  ) {}

  ngOnInit(): void {
    this.availableColumnsForExport$ = this.exportReportsService.currentStatisticsSection$.pipe(
      switchMap(currentStatisticsSection => this.exportReportsService.getAvailableColumnsForExport(currentStatisticsSection)),
      switchMap(availableColumnsForExport => this.exportReportsService.tableColumns$.pipe(map(tableColumns => ({ tableColumns, availableColumnsForExport })))),
      map(({ availableColumnsForExport, tableColumns }) =>
        availableColumnsForExport.map(columnName => ({
          name: columnName,
          checked: tableColumns.find(({ columnNameForRaportExport }) => columnNameForRaportExport === columnName)?.checked ?? false
        }))
      ),
      tap(columns => (this.checkedValues = columns.filter(({ checked }) => checked).map(({ name }) => name)))
    );
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.selectOptions.length) {
      this.selectedFormat = this.selectOptions[0].name as AvailableReportsFormats;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public handleSwitch = (event: IToggleSwitchStateData) => {
    const { state } = event;

    this.saveAsReport = state;
  };

  public checkboxHandler(column: { name: string; checked: boolean }, checked: boolean): void {
    if (checked && column.name) {
      this.checkedValues = [...this.checkedValues, column.name];
      return;
    }

    this.checkedValues = [...this.checkedValues].filter(value => value !== column.name);
  }

  public getSelectedValue(value: Record<string, any>): void {
    this.selectedFormat = value.choosenValue.name;
  }

  public exportReport = () => {
    if (!this.checkedValues.length) return;

    this.loading = true;

    this.exportReportsService.currentStatisticsSection$
      .pipe(
        switchMap(type => this.statisticsTableService.getSortMethod().pipe(map(sortMethod => ({ type, sortMethod })))),
        switchMap(({ sortMethod, type }) =>
          this.statisticsService.watchStatisticsRequestParams$().pipe(map(requestParams => ({ requestParams, sortMethod, type })))
        ),
        switchMap(({ requestParams, sortMethod, type }) => {
          const { filter_columns, order_field, table_columns } = this.exportReportsService.prepareExportReportConfig(
            requestParams,
            sortMethod,
            this.checkedValues
          );
          return this.exportReportsService.exportReport({
            type: type,
            format: this.selectedFormat,
            filter_columns,
            order_field,
            table_columns,
            delete: !this.saveAsReport
          });
        }),
        tap(({ status_code }) => {
          this.showNotification(status_code === 201 ? 'statistics_report_export_success' : 'error_occured', status_code !== 201);

          this.loading = false;

          if (status_code === 201) this.activeModal.close();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(
        () => {},
        () => {
          this.showNotification('error_occured', true);
          this.loading = false;
        }
      );
  };

  private showNotification(message: string, error: boolean): void {
    const type = error ? NotificationTypes[1] : NotificationTypes[0];

    this.globalNotificationsService.show({
      message: `view_translations.${message}`,
      type,
      translate: true,
      isPersistent: false,
      id: ''
    });
  }
}
